import { useState } from "react";
import { Field } from "@/components/shared";
import { eur } from "@/lib/format";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import AuthImage from "@/components/AuthImage";
import {
  PROPERTY_STATUSES, PROPERTY_TYPES, ENERGY_CLASSES, EXPOSURES, HEATING_TYPES,
  GARDEN_TYPES, CONDITIONS, DOC_CHECKLIST, CONTACT_ROLES,
} from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Link2, X, Upload, GripVertical, Star } from "lucide-react";

export const num = (v) => Number(v) || 0;
export const int = (v) => Math.max(0, parseInt(v, 10) || 0);

export const EMPTY = {
  address: "", city: "Reggio Emilia", province: "RE", cap: "", property_type: "Appartamento",
  owner: "", cadastral_info: "", status: "Nuova opportunità",
  linked_contact_ids: [], linked_contacts: [], responsible: "",
  sqm_commercial: 0, sqm_walkable: 0, rooms: 0, bedrooms: 0, bathrooms: 0,
  floor: "", total_floors: 0, elevator: false, year_built: "", condition: "",
  exposure: "", heating: "", energy_class: "",
  balconies: 0, balconies_sqm: 0, terraces: 0, terraces_sqm: 0, garden_type: "",
  garage: false, garage_sqm: 0, parking_spot: false, cellar: false, attic: false,
  dependance: "", land: "", other_appurtenances: "",
  asking_price: 0, proposed_price: 0, purchase_cost: 0, renovation_cost: 0, estimated_resale_value: 0,
  documents: DOC_CHECKLIST.map((n) => ({ name: n, done: false })),
  photos: [], videos: [], photo_captions: {}, technical_notes: "", legal_notes: "", notes: "",
  amount_received: 0, total_costs: 0, closing_date: "",
};

export const buildPayload = (form) => ({
  ...form,
  asking_price: num(form.asking_price), proposed_price: num(form.proposed_price),
  purchase_cost: num(form.purchase_cost), renovation_cost: num(form.renovation_cost),
  estimated_resale_value: num(form.estimated_resale_value),
  amount_received: num(form.amount_received), total_costs: num(form.total_costs),
  sqm_commercial: num(form.sqm_commercial), sqm_walkable: num(form.sqm_walkable),
  balconies_sqm: num(form.balconies_sqm), terraces_sqm: num(form.terraces_sqm), garage_sqm: num(form.garage_sqm),
  rooms: int(form.rooms), bedrooms: int(form.bedrooms), bathrooms: int(form.bathrooms),
  balconies: int(form.balconies), terraces: int(form.terraces), total_floors: int(form.total_floors),
  garden: (form.garden_type || "") === "Privato" || (form.garden_type || "") === "Condominiale",
  photos: (form.photos || []).filter(Boolean),
  videos: (form.videos || []).filter(Boolean),
  responsible: form.responsible || null,
});

export const estimatedProfit = (form) =>
  num(form.estimated_resale_value) - num(form.purchase_cost) - num(form.renovation_cost);

export function sheetCompletion(p) {
  if (!p) return 0;
  const docsDone = (p.documents || []).filter((d) => d.done).length;
  const hasContact = !!(p.owner && String(p.owner).trim()) || (p.linked_contact_ids || []).length > 0 || (p.contacts || []).length > 0 || (p.linked_contacts || []).length > 0;
  const checks = [
    !!(p.address && String(p.address).trim()),
    !!(p.city && String(p.city).trim()),
    !!(p.property_type && String(p.property_type).trim()),
    hasContact,
    num(p.asking_price) > 0 || num(p.proposed_price) > 0,
    num(p.sqm_commercial) > 0,
    int(p.rooms) > 0,
    !!(p.energy_class && String(p.energy_class).trim()),
    (p.photos || []).filter(Boolean).length >= 1,
    docsDone >= 3,
  ];
  return Math.round((checks.filter(Boolean).length / checks.length) * 100);
}

export const propertyFromRecord = (p) => {
  const docs = DOC_CHECKLIST.map((n) => (p.documents || []).find((d) => d.name === n) || { name: n, done: false });
  const links = (p.linked_contacts && p.linked_contacts.length)
    ? p.linked_contacts
    : (p.linked_contact_ids || []).map((cid) => ({ contact_id: cid, role: "Proprietario" }));
  return { ...EMPTY, ...p, documents: docs, responsible: p.responsible || "", linked_contacts: links, photos: p.photos || [], videos: p.videos || [] };
};

function SectionTitle({ children }) {
  return <div className="sm:col-span-2 mt-2 mb-1 border-b border-border pb-1 text-sm font-bold uppercase tracking-wide text-primary">{children}</div>;
}

function NumField({ label, value, onChange, testid, suffix }) {
  return (
    <Field label={label}>
      <div className="relative">
        <Input type="number" value={value} onChange={onChange} data-testid={testid} />
        {suffix && <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">{suffix}</span>}
      </div>
    </Field>
  );
}

const AMENITIES = [["elevator", "Ascensore"], ["parking_spot", "Posto auto"], ["cellar", "Cantina"], ["attic", "Soffitta / Mansarda"]];

export default function PropertyForm({ form, setForm, isAdmin, users = [], contacts = [] }) {
  const [contactSearch, setContactSearch] = useState("");
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const onUpload = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of list) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(r.data.path);
      }
      setForm({ ...form, photos: [...(form.photos || []).filter(Boolean), ...uploaded] });
      toast.success(`${uploaded.length} foto caricata/e`);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Caricamento non riuscito"); }
    finally { setUploading(false); }
  };
  const removePhoto = (src) => setForm({ ...form, photos: (form.photos || []).filter((p) => p !== src) });
  const [dragIdx, setDragIdx] = useState(null);
  const reorderPhotos = (from, to) => {
    const list = (form.photos || []).filter(Boolean);
    if (from == null || to == null || from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return;
    const next = [...list];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setForm({ ...form, photos: next });
  };
  const setCover = (i) => { if (i > 0) { reorderPhotos(i, 0); toast.success("Foto impostata come copertina"); } };
  const setCaption = (src, val) => setForm({ ...form, photo_captions: { ...(form.photo_captions || {}), [src]: val } });

  const addLinkedContact = (cid) => {
    if (form.linked_contacts.some((l) => l.contact_id === cid)) return;
    setForm({ ...form, linked_contacts: [...form.linked_contacts, { contact_id: cid, role: "Proprietario" }] });
    setContactSearch("");
  };
  const setLinkRole = (cid, role) => setForm({ ...form, linked_contacts: form.linked_contacts.map((l) => (l.contact_id === cid ? { ...l, role } : l)) });
  const removeLinked = (cid) => setForm({ ...form, linked_contacts: form.linked_contacts.filter((l) => l.contact_id !== cid) });
  const contactById = (cid) => contacts.find((c) => c.id === cid);
  const contactResults = contactSearch.trim()
    ? contacts.filter((c) => !form.linked_contacts.some((l) => l.contact_id === c.id) && `${c.name} ${c.company || ""}`.toLowerCase().includes(contactSearch.toLowerCase())).slice(0, 6)
    : [];
  const toggleDoc = (i) => { const d = [...form.documents]; d[i] = { ...d[i], done: !d[i].done }; setForm({ ...form, documents: d }); };

  return (
    <div className="grid sm:grid-cols-2 gap-4 py-2" data-testid="property-form">
      {/* DATI IDENTIFICATIVI */}
      <SectionTitle>Dati identificativi</SectionTitle>
      <Field label="Indirizzo completo *" className="sm:col-span-2"><Input value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="property-address" placeholder="Via, civico, interno" /></Field>
      <Field label="Comune"><Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="property-city" /></Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Provincia"><Input value={form.province} onChange={(e) => set("province", e.target.value)} data-testid="property-province" placeholder="RE" /></Field>
        <Field label="CAP"><Input value={form.cap} onChange={(e) => set("cap", e.target.value)} data-testid="property-cap" placeholder="42100" /></Field>
      </div>
      <Field label="Tipologia immobile">
        <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
          <SelectTrigger data-testid="property-type"><SelectValue /></SelectTrigger>
          <SelectContent>{PROPERTY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Stato dell'immobile">
        <Select value={form.status} onValueChange={(v) => set("status", v)}>
          <SelectTrigger data-testid="property-status"><SelectValue /></SelectTrigger>
          <SelectContent>{PROPERTY_STATUSES.filter((s) => s !== "Scartata").map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Proprietario"><Input value={form.owner} onChange={(e) => set("owner", e.target.value)} data-testid="property-owner" /></Field>
      <Field label="Dati catastali"><Input value={form.cadastral_info} onChange={(e) => set("cadastral_info", e.target.value)} placeholder="Fg. — Part. — Sub. —" /></Field>
      {isAdmin && (
        <Field label="Responsabile">
          <Select value={form.responsible || undefined} onValueChange={(v) => set("responsible", v)}>
            <SelectTrigger data-testid="property-responsible"><SelectValue placeholder="Seleziona responsabile" /></SelectTrigger>
            <SelectContent>{users.filter((u) => u.assignable !== false && u.role !== "admin").map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      )}
      <Field label="Proprietario e contatti collegati" className="sm:col-span-2">
        <div className="space-y-3" data-testid="linked-contacts-manager">
          <div className="relative">
            <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input className="pl-9" placeholder="Cerca un contatto per nome o azienda..." value={contactSearch} onChange={(e) => setContactSearch(e.target.value)} data-testid="linked-contact-search" />
            {contactResults.length > 0 && (
              <div className="absolute z-20 mt-1 w-full rounded-lg border border-border bg-white shadow-lg overflow-hidden" data-testid="linked-contact-results">
                {contactResults.map((c) => (
                  <button type="button" key={c.id} className="w-full text-left px-3 py-2 text-sm hover:bg-muted/60 flex items-center justify-between" onClick={() => addLinkedContact(c.id)} data-testid={`link-contact-option-${c.id}`}>
                    <span className="font-medium text-slate-800">{c.name}</span><span className="text-xs text-muted-foreground">{c.company || "—"}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-2">
            {form.linked_contacts.length === 0 && <div className="text-sm text-muted-foreground">Nessun contatto collegato.</div>}
            {form.linked_contacts.map((l) => { const c = contactById(l.contact_id); return (
              <div key={l.contact_id} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2" data-testid={`linked-contact-row-${l.contact_id}`}>
                <div className="flex-1 min-w-0"><div className="text-sm font-medium text-slate-800 truncate">{c ? c.name : "Contatto"}</div>{c?.company && <div className="text-xs text-muted-foreground truncate">{c.company}</div>}</div>
                <Select value={l.role} onValueChange={(v) => setLinkRole(l.contact_id, v)}>
                  <SelectTrigger className="w-48 h-9" data-testid={`linked-contact-role-${l.contact_id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{CONTACT_ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
                <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-rose-500" onClick={() => removeLinked(l.contact_id)} data-testid={`remove-linked-contact-${l.contact_id}`}><X className="h-4 w-4" /></Button>
              </div>
            ); })}
          </div>
        </div>
      </Field>

      {/* CARATTERISTICHE */}
      <SectionTitle>Caratteristiche</SectionTitle>
      <NumField label="Mq commerciali" value={form.sqm_commercial} onChange={(e) => set("sqm_commercial", e.target.value)} testid="property-sqm-commercial" suffix="m²" />
      <NumField label="Mq calpestabili" value={form.sqm_walkable} onChange={(e) => set("sqm_walkable", e.target.value)} testid="property-sqm-walkable" suffix="m²" />
      <NumField label="Numero locali" value={form.rooms} onChange={(e) => set("rooms", e.target.value)} testid="property-rooms" />
      <NumField label="Numero camere" value={form.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} testid="property-bedrooms" />
      <NumField label="Numero bagni" value={form.bathrooms} onChange={(e) => set("bathrooms", e.target.value)} testid="property-bathrooms" />
      <Field label="Piano"><Input value={form.floor} onChange={(e) => set("floor", e.target.value)} data-testid="property-floor" placeholder="Es. 2° / Piano terra" /></Field>
      <NumField label="Numero totale piani" value={form.total_floors} onChange={(e) => set("total_floors", e.target.value)} testid="property-total-floors" />
      <Field label="Anno di costruzione"><Input value={form.year_built} onChange={(e) => set("year_built", e.target.value)} data-testid="property-year" placeholder="Es. 1998" /></Field>
      <Field label="Stato di conservazione">
        <Select value={form.condition || undefined} onValueChange={(v) => set("condition", v)}>
          <SelectTrigger data-testid="property-condition"><SelectValue placeholder="Seleziona" /></SelectTrigger>
          <SelectContent>{CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Esposizione">
        <Select value={form.exposure || undefined} onValueChange={(v) => set("exposure", v)}>
          <SelectTrigger data-testid="property-exposure"><SelectValue placeholder="Seleziona" /></SelectTrigger>
          <SelectContent>{EXPOSURES.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Riscaldamento">
        <Select value={form.heating || undefined} onValueChange={(v) => set("heating", v)}>
          <SelectTrigger data-testid="property-heating"><SelectValue placeholder="Seleziona" /></SelectTrigger>
          <SelectContent>{HEATING_TYPES.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Classe energetica">
        <Select value={form.energy_class || undefined} onValueChange={(v) => set("energy_class", v)}>
          <SelectTrigger data-testid="property-energy"><SelectValue placeholder="Seleziona" /></SelectTrigger>
          <SelectContent>{ENERGY_CLASSES.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Ascensore" className="sm:col-span-2">
        <label className="flex w-fit items-center gap-2 rounded-lg border border-border px-3 py-2 cursor-pointer hover:bg-muted/50" data-testid="amenity-elevator">
          <Checkbox checked={!!form.elevator} onCheckedChange={() => set("elevator", !form.elevator)} /><span className="text-sm">Ascensore presente</span>
        </label>
      </Field>

      {/* SPAZI ESTERNI E PERTINENZE */}
      <SectionTitle>Balconi, spazi esterni e pertinenze</SectionTitle>
      <NumField label="Numero balconi" value={form.balconies} onChange={(e) => set("balconies", e.target.value)} testid="property-balconies" />
      <NumField label="Mq balconi" value={form.balconies_sqm} onChange={(e) => set("balconies_sqm", e.target.value)} testid="property-balconies-sqm" suffix="m²" />
      <NumField label="Numero terrazzi" value={form.terraces} onChange={(e) => set("terraces", e.target.value)} testid="property-terraces" />
      <NumField label="Mq terrazzi" value={form.terraces_sqm} onChange={(e) => set("terraces_sqm", e.target.value)} testid="property-terraces-sqm" suffix="m²" />
      <Field label="Giardino">
        <Select value={form.garden_type || undefined} onValueChange={(v) => set("garden_type", v)}>
          <SelectTrigger data-testid="property-garden-type"><SelectValue placeholder="Nessuno" /></SelectTrigger>
          <SelectContent>{GARDEN_TYPES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <NumField label="Mq garage" value={form.garage_sqm} onChange={(e) => set("garage_sqm", e.target.value)} testid="property-garage-sqm" suffix="m²" />
      <Field label="Pertinenze" className="sm:col-span-2">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[["garage", "Garage"], ...AMENITIES].map(([k, lbl]) => (
            <label key={k} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 cursor-pointer hover:bg-muted/50" data-testid={`amenity-${k}`}>
              <Checkbox checked={!!form[k]} onCheckedChange={() => set(k, !form[k])} /><span className="text-sm">{lbl}</span>
            </label>
          ))}
        </div>
      </Field>
      <Field label="Dépendance"><Input value={form.dependance} onChange={(e) => set("dependance", e.target.value)} data-testid="property-dependance" placeholder="Es. dépendance 30 m²" /></Field>
      <Field label="Terreno"><Input value={form.land} onChange={(e) => set("land", e.target.value)} data-testid="property-land" placeholder="Es. terreno 500 m²" /></Field>
      <Field label="Altre pertinenze" className="sm:col-span-2"><Input value={form.other_appurtenances} onChange={(e) => set("other_appurtenances", e.target.value)} data-testid="property-other-appurtenances" placeholder="Note pertinenze aggiuntive" /></Field>

      {/* DATI ECONOMICI */}
      <SectionTitle>Dati economici</SectionTitle>
      <NumField label="Prezzo richiesto" value={form.asking_price} onChange={(e) => set("asking_price", e.target.value)} testid="property-asking" suffix="€" />
      <NumField label="Prezzo proposto" value={form.proposed_price} onChange={(e) => set("proposed_price", e.target.value)} testid="property-proposed" suffix="€" />
      <NumField label="Costo di acquisto" value={form.purchase_cost} onChange={(e) => set("purchase_cost", e.target.value)} testid="property-purchase-cost" suffix="€" />
      <NumField label="Costo stimato ristrutturazione" value={form.renovation_cost} onChange={(e) => set("renovation_cost", e.target.value)} testid="property-renovation-cost" suffix="€" />
      <NumField label="Valore stimato di rivendita" value={form.estimated_resale_value} onChange={(e) => set("estimated_resale_value", e.target.value)} testid="property-resale-value" suffix="€" />
      <Field label="Utile stimato (calcolato)">
        <div className={`flex h-10 items-center rounded-md border border-dashed border-border bg-muted/40 px-3 text-sm font-mono font-semibold ${estimatedProfit(form) >= 0 ? "text-emerald-700" : "text-rose-600"}`} data-testid="property-estimated-profit">
          {eur(estimatedProfit(form))}
        </div>
      </Field>

      {/* DOCUMENTAZIONE E CONTENUTI */}
      <SectionTitle>Documentazione e contenuti</SectionTitle>
      <Field label="Documentazione" className="sm:col-span-2">
        <div className="grid grid-cols-2 gap-2">
          {form.documents.map((d, i) => (
            <label key={d.name} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 cursor-pointer hover:bg-muted/50" data-testid={`doc-${i}`}>
              <Checkbox checked={d.done} onCheckedChange={() => toggleDoc(i)} /><span className="text-sm">{d.name}</span>
            </label>
          ))}
        </div>
      </Field>
      <Field label="Fotografie" className="sm:col-span-2">
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <label className="inline-flex items-center gap-2 rounded-lg border border-dashed border-border px-4 py-2 cursor-pointer hover:bg-muted/50 text-sm font-medium" data-testid="photo-upload-label">
              <Upload className="h-4 w-4" />{uploading ? "Caricamento..." : "Carica foto (JPG/PNG/WEBP)"}
              <input type="file" accept="image/png,image/jpeg,image/webp" multiple className="hidden" disabled={uploading} onChange={(e) => { onUpload(e.target.files); e.target.value = ""; }} data-testid="photo-upload-input" />
            </label>
            <span className="text-xs text-muted-foreground">{(form.photos || []).filter(Boolean).length} foto</span>
          </div>
          {(form.photos || []).filter(Boolean).length > 0 && (
            <>
              <p className="text-xs text-muted-foreground">Trascina le foto per riordinarle. La prima è la <strong>copertina</strong> usata nelle liste e nel PDF.</p>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {(form.photos || []).filter(Boolean).map((src, i) => (
                  <div key={src} className="space-y-1">
                    <div
                      draggable
                      onDragStart={() => setDragIdx(i)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => { e.preventDefault(); reorderPhotos(dragIdx, i); setDragIdx(null); }}
                      onDragEnd={() => setDragIdx(null)}
                      className={`relative aspect-square rounded-lg overflow-hidden border bg-muted group cursor-move ${i === 0 ? "border-primary ring-2 ring-primary/30" : "border-border"} ${dragIdx === i ? "opacity-40" : ""}`}
                      data-testid={`form-photo-${i}`}
                    >
                      <AuthImage src={src} className="h-full w-full object-cover pointer-events-none" />
                      {i === 0 && <span className="absolute bottom-1 left-1 rounded bg-primary px-1.5 py-0.5 text-[10px] font-bold text-white" data-testid="cover-badge">COPERTINA</span>}
                      <GripVertical className="absolute top-1 left-1 h-4 w-4 text-white/90 drop-shadow opacity-0 group-hover:opacity-100 transition-opacity" />
                      {i !== 0 && (
                        <button type="button" onClick={() => setCover(i)} title="Imposta come copertina" className="absolute bottom-1 right-1 rounded-full bg-black/60 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-primary" data-testid={`set-cover-${i}`}><Star className="h-3 w-3" /></button>
                      )}
                      <button type="button" onClick={() => removePhoto(src)} className="absolute top-1 right-1 rounded-full bg-black/60 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-photo-${i}`}><X className="h-3 w-3" /></button>
                    </div>
                    <Input value={(form.photo_captions || {})[src] || ""} onChange={(e) => setCaption(src, e.target.value)} placeholder="Didascalia…" className="h-7 text-xs" data-testid={`photo-caption-${i}`} maxLength={80} />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </Field>
      <Field label="Video (una URL per riga)" className="sm:col-span-2"><Textarea value={(form.videos || []).join("\n")} onChange={(e) => set("videos", e.target.value.split("\n"))} data-testid="property-videos" placeholder="https://..." /></Field>
      <Field label="Note tecniche" className="sm:col-span-2"><Textarea value={form.technical_notes} onChange={(e) => set("technical_notes", e.target.value)} data-testid="property-technical-notes" /></Field>
      <Field label="Note legali" className="sm:col-span-2"><Textarea value={form.legal_notes} onChange={(e) => set("legal_notes", e.target.value)} data-testid="property-legal-notes" /></Field>
      <Field label="Note interne" className="sm:col-span-2"><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="property-internal-notes" placeholder="Note ad uso interno" /></Field>
    </div>
  );
}
